"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Users,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Tag,
  BookUser,
  FileText,
  CreditCard,
  Search,
  Settings,
  MessageSquare,
  Layers,
  ShoppingCart,
  Clock,
  BarChart3,
  MessageSquareQuote,
} from "lucide-react";

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  href?: string;
  children?: { label: string; href: string; icon: React.ReactNode }[];
}

const menuItems: MenuItem[] = [
  {
    label: "Dashboard",
    icon: <LayoutDashboard size={18} />,
    href: "/admin",
  },
  {
    label: "Master",
    icon: <BookUser size={18} />,
    children: [
      { label: "User", href: "/admin/master/user", icon: <Users size={16} /> },
      { label: "CMS", href: "/admin/master/cms", icon: <FileText size={16} /> },
      {
        label: "Category",
        href: "/admin/master/category",
        icon: <FileText size={16} />,
      },
      {
        label: "Discount",
        href: "/admin/master/discount",
        icon: <Tag size={16} />,
      },
      {
        label: "Price Range",
        href: "/admin/master/price-range",
        icon: <Layers size={16} />,
      },
      {
        label: "Blog",
        href: "/admin/master/blog",
        icon: <FileText size={16} />,
      },
      {
        label: "SEO Tag",
        href: "/admin/master/seotag",
        icon: <Search size={16} />,
      },
      {
        label: "Customer",
        href: "/admin/master/customer",
        icon: <FileText size={16} />,
      },
      {
        label: "Payment Method",
        href: "/admin/master/payment-method",
        icon: <CreditCard size={16} />,
      },
      {
        label: "Testimonial",
        href: "/admin/master/testimonial",
        icon: <MessageSquareQuote size={16} />,
      },
      {
        label: "Setting",
        href: "/admin/master/setting",
        icon: <Settings size={16} />,
      },
    ],
  },
  {
    label: "Product",
    icon: <ShoppingBag size={18} />,
    href: "/admin/product",
  },
  {
    label: "Orders",
    icon: <FileText size={18} />,
    href: "/admin/orders",
  },
  {
    label: "Temporary Booking",
    icon: <Clock size={18} />,
    href: "/admin/temp-bookings",
  },
  {
    label: "Pending Carts",
    icon: <ShoppingCart size={18} />,
    href: "/admin/pending-carts",
  },
  {
    label: "Inquiry",
    icon: <MessageSquare size={18} />,
    href: "/admin/inquiry",
  },
  {
    label: "Report",
    icon: <BarChart3 size={18} />,
    children: [
      {
        label: "Order Report",
        href: "/admin/report/order",
        icon: <FileText size={16} />,
      },
    ],
  },
];

const STORAGE_KEY = "admin_sidebar_collapsed";

export default function AdminSidebar() {
  const pathname = usePathname();
  // Default to true (drawer/icon mode)
  const [collapsed, setCollapsed] = useState(true);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      setCollapsed(saved === "true");
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      if (next) setOpenMenus({}); // Close all submenus when collapsing
      return next;
    });
  }, []);

  const toggleMenu = useCallback((label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const closeOnMobile = useCallback(() => {
    if (window.innerWidth < 768) {
      setCollapsed(true);
    }
  }, []);

  const isActive = (href: string) => pathname === href;
  const isParentActive = (item: MenuItem) =>
    item.children?.some((c) => pathname === c.href);

  return (
    <>
      <aside
        className={`relative flex flex-col bg-white border-r border-border-1 transition-all duration-300 ease-in-out flex-shrink-0 z-20 no-print ${
          collapsed
            ? "w-[68px]"
            : "w-[240px] shadow-sm absolute md:relative h-full"
        }`}
      >
        {/* Logo area */}
        <div className="flex items-center h-16 px-4 border-b border-border-1 overflow-hidden shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0 w-full">
            <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-gold-dim via-gold-core to-gold-glow flex items-center justify-center flex-shrink-0 shadow-sm border border-gold-dim/20">
              <span className="text-white font-bold text-sm tracking-widest">
                N
              </span>
            </div>
            {!collapsed && (
              <div
                className="min-w-0 overflow-hidden opacity-0 animate-fadeIn"
                style={{
                  animationFillMode: "forwards",
                  animationDelay: "100ms",
                }}
              >
                <div className="font-cormorant font-bold text-text-1 text-lg truncate leading-tight tracking-wide">
                  NakMan
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-text-3 truncate leading-tight">
                  Admin Panel
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav 
            Fixed overflow-y-auto causing horizontal scrolling in collapsed mode by dynamically applying overflow.
        */}
        <nav
          className={`flex-1 py-6 px-2 bg-white ${
            collapsed
              ? "overflow-visible"
              : "overflow-y-auto overflow-x-hidden custom-scrollbar"
          }`}
        >
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const active = item.href
                ? isActive(item.href)
                : isParentActive(item);
              const isOpen = openMenus[item.label] || isParentActive(item);

              return (
                <li key={item.label} className="relative group/menuitem">
                  {item.href ? (
                    <Link
                      href={item.href}
                      onClick={closeOnMobile}
                      className={`flex items-center gap-3 px-3 py-3 rounded-md text-xs uppercase tracking-wider font-bold transition-all duration-200 ${
                        active
                          ? "bg-gold-core text-white border border-gold-dim/20 shadow-sm"
                          : "text-text-3 hover:bg-black-2 hover:text-text-1 border border-transparent"
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 ${
                          active
                            ? "text-white"
                            : "text-slate-400 group-hover/menuitem:text-gold-dim"
                        }`}
                      >
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </Link>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          if (collapsed) {
                            setCollapsed(false);
                            localStorage.setItem(STORAGE_KEY, "false");
                            setOpenMenus((prev) => ({
                              ...prev,
                              [item.label]: true,
                            }));
                          } else {
                            toggleMenu(item.label);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-md text-xs uppercase tracking-wider font-bold transition-all duration-200 ${
                          active
                            ? "bg-black-2 text-gold-dim border border-border-1"
                            : "text-text-3 hover:bg-black-2 hover:text-text-1 border border-transparent"
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 ${
                            active
                              ? "text-gold-core"
                              : "text-slate-400 group-hover/menuitem:text-gold-dim"
                          }`}
                        >
                          {item.icon}
                        </span>
                        {!collapsed && (
                          <>
                            <span className="truncate flex-1 text-left">
                              {item.label}
                            </span>
                            <span className="text-slate-400 flex-shrink-0 transition-transform duration-200">
                              {isOpen ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </span>
                          </>
                        )}
                      </button>

                      {/* Submenu */}
                      {!collapsed && item.children && (
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isOpen
                              ? "max-h-96 opacity-100 mt-2"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <ul className="ml-5 pl-3 border-l-2 border-border-1 py-1 space-y-1">
                            {item.children.map((child) => (
                              <li key={child.href}>
                                <Link
                                  href={child.href}
                                  onClick={closeOnMobile}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-[10px] uppercase font-bold tracking-widest transition-all duration-200 ${
                                    isActive(child.href)
                                      ? "bg-gold-core/5 text-gold-dim"
                                      : "text-slate-500 hover:bg-slate-50 hover:text-gold-dim"
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {/* Tooltip for collapsed mode (Hidden on mobile)
                      Moved outside of button/Link to avoid hydration nesting warnings and enable interactive links inside the tooltip!
                      Included a transparent pl-3 to act as an invisible bridge so the mouse can travel to the tooltip without closing it.
                  */}
                  {collapsed && (
                    <div className="hidden md:block absolute left-full top-0 pl-3 z-50 opacity-0 invisible group-hover/menuitem:opacity-100 group-hover/menuitem:visible transition-all duration-200">
                      <div className="bg-white border border-border-1 text-text-2 text-[10px] tracking-widest rounded-sm whitespace-nowrap shadow-xl px-3 py-2 pointer-events-auto">
                        <span className="font-bold block mb-1">
                          {item.label}
                        </span>
                        {item.children && (
                          <div className="mt-2 pl-2 border-l border-slate-200 flex flex-col gap-1.5">
                            {item.children.map((child) => (
                              <Link
                                key={child.href}
                                href={child.href}
                                className="text-slate-500 text-[9px] uppercase tracking-wider font-semibold text-start hover:text-gold-dim transition-colors py-1 block"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Collapse toggle button */}
        <button
          type="button"
          onClick={toggleCollapse}
          className="absolute -right-3 top-20 z-10 w-6 h-6 bg-white border border-border-1 rounded-full flex items-center justify-center shadow-sm hover:bg-black-2 hover:text-gold-dim text-text-3 transition-colors hidden md:flex"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Mobile overlay */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-10 md:hidden backdrop-blur-sm animate-fadeIn no-print"
          onClick={() => setCollapsed(true)}
        />
      )}
    </>
  );
}
